<template>
	<div class="home-page" :class="{ 'menu-open': menuOpen }">
		<!-- 顶部导航 -->
		<Header
			:showLogo="true"
			theme="dark"
			:fixed="true"
			:showBorder="isScrolled"
			backgroundColor="transparent"
			@menu-open="menuOpen = true"
			@menu-close="menuOpen = false"
			iconColor="white"
			:logoShrink="isScrolled"
		/>

		<!-- Hero Section - 支持单图/双图模式 -->
		<section class="hero-section" @click="onViewAllClick()">
			<!-- 双图模式：左右各一张 -->
			<template v-if="bannerConfig.DisplayMode === 2">
				<div class="hero-image-left">
					<img :src="bannerConfig.LeftImageUrl || defaultLeftImage" alt="Hero Left">
				</div>
				<div class="hero-image-right">
					<img :src="bannerConfig.RightImageUrl || defaultRightImage" alt="Hero Right">
				</div>
			</template>
			<!-- 单图模式：只显示一张 -->
			<template v-else>
				<div class="hero-image-single">
					<img :src="bannerConfig.ImageUrl || defaultImage" alt="Hero Banner">
				</div>
			</template>

			<!-- Hero 加载遮罩 -->
			<!-- <div v-if="loading" class="hero-loading-overlay">
				<div class="hero-spinner"></div>
				<p class="hero-loading-text">Loading...</p>
			</div> -->

			<!-- 内容层 - 居中靠下 -->
			<div class="hero-content">
				<h2 class="hero-subtitle font-apercu-medium" style="cursor: pointer;font-size: 12rem;">Discover Our Full Collection</h2>
				<h1 class="hero-title font-bespoke" style="cursor: pointer;font-size: 24rem;line-height: 17rem;">Essential for Your Wellbeing</h1>
			</div>
		</section>

		<!-- Collection Grid - 动态从接口获取 -->
		<section class="collection-grid" v-if="homepageCategories.length > 0">
			<div class="grid-item" v-for="item in homepageCategories" :key="item.CategoryID">
				<div class="image-wrapper">
					<img 
						:src="item.CategoryImage || defaultImage" 
						:alt="item.CategoryName" 
						@click="goToCategory(item.CategoryID, item.CategoryName)"
					>
				</div>
				<p class="item-name font-apercu-medium">{{ item.CategoryName }}</p>
			</div>
		</section>

		<!-- 加载状态（骨架屏） -->
		<section class="collection-grid" v-else-if="loading">
			<div class="grid-item" v-for="i in 4" :key="'skeleton-' + i">
				<div class="image-wrapper skeleton"></div>
				<p class="item-name skeleton-text">&nbsp;</p>
			</div>
		</section>

		<!-- Project Banner -->
		<section class="project-banner" @click="goToProjects" :style="{ backgroundImage: `url(${projectBannerImage})` }"
         style="cursor: pointer;">
			<div class="banner-content">
				<h2 class="banner-subtitle font-apercu-medium" style="font-size: 12rem;">Discover Projects</h2>
				<h1 class="banner-title font-bespoke" style="font-size: 24rem;line-height: 17rem;">You're Allowed to Dream</h1>
			</div>
		</section>

		<!-- Two Column Section -->
		<section class="two-column-section">
			<div class="column-item" @click="goToMaterials" style="cursor: pointer;">
				<div class="column-image-wrapper">
					<img :src="materialsImage" alt="denHolm Materials">
				</div>
				<div class="column-text-wrapper" style="margin-top: -74rem;color: #fff;">
					<h3 class="column-title font-apercu-bold">denHolm Materials</h3>
					<p class="column-subtitle font-apercu-medium" style="color: #fff;">Discover Our Exclusive Material Range</p>
				</div>
			</div>
			<div class="column-item" @click="goToStories" style="cursor: pointer;">
				<div class="column-image-wrapper">
					<img :src="storiesImage" alt="Stories">
				</div>
				<div class="column-text-wrapper" style="margin-top: -74rem;color: #fff;">
					<h3 class="column-title font-apercu-bold">Stories</h3>
					<p class="column-subtitle font-apercu-medium" style="color: #fff;">Discover Behind the Scenes Stories</p>
				</div>
			</div>
		</section>

		<Footer layout="about" :showDecoration="true" :showStudio="true" :showInstagram="true"
			style="margin-top: 99rem;" />
	</div>
</template>

<script>
	import Header from '../components/Header.vue'
	import Footer from '../components/Footer.vue'
	import { getHomepageConfig, getHomepageCategories } from '@/api/home/home'

	// ============ 本地默认资源（使用 require 确保 webpack 正确处理） ============
	const HOME_DEFAULT = require('../assets/home_bg.jpg')
	const PROJECT_BANNER = require('../assets/home/bg2.jpg')
	const MATERIALS_IMAGE = require('../assets/46830015 1.png')
	const STORIES_IMAGE = require('../assets/Stories.jpg')
	const DEFAULT_LEFT = require('../assets/home_bg_left.png')
	const DEFAULT_RIGHT = require('../assets/home_bg_right.png')
	const DEFAULT_SLIDER1 = require('../assets/home/home-silder1.png')
	const DEFAULT_SLIDER2 = require('../assets/home/home-silder2.png')
	const DEFAULT_SLIDER3 = require('../assets/home/home-silder3.png')
	const DEFAULT_SLIDER4 = require('../assets/home/home-silder4.png')

	// 默认四个分类（本地资源，页面一加载立即显示）
	const DEFAULT_CATEGORIES = [
		{ CategoryID: 80, CategoryName: 'Atelier', CategoryImage: DEFAULT_SLIDER1 },
		{ CategoryID: 81, CategoryName: 'Product', CategoryImage: DEFAULT_SLIDER2 },
		{ CategoryID: 82, CategoryName: 'Sunshine', CategoryImage: DEFAULT_SLIDER3 },
		{ CategoryID: 83, CategoryName: 'denHolm United', CategoryImage: DEFAULT_SLIDER4 }
	]

	export default {
		name: 'Home',
		components: {
			Header,
			Footer
		},
		data() {
			return {
				menuOpen: false,
				isScrolled: false,
				loading: false,

				// Banner 配置（默认使用本地资源）
				bannerConfig: {
					DisplayMode: 2,
					ImageUrl: HOME_DEFAULT,
					LeftImageUrl: DEFAULT_LEFT,
					RightImageUrl: DEFAULT_RIGHT
				},

				// 首页四个分类（默认使用本地资源）
				homepageCategories: DEFAULT_CATEGORIES,

				// 静态资源（用于回退）
				defaultImage: HOME_DEFAULT,
				defaultLeftImage: DEFAULT_LEFT,
				defaultRightImage: DEFAULT_RIGHT,
				projectBannerImage: PROJECT_BANNER,
				materialsImage: MATERIALS_IMAGE,
				storiesImage: STORIES_IMAGE
			}
		},
		methods: {
			// ============ 数据获取（接口返回后平滑覆盖默认值） ============
			async fetchHomepageData() {
				this.loading = true
				try {
					const [configRes, categoriesRes] = await Promise.all([
						getHomepageConfig(),
						getHomepageCategories(8)
					])

					// 接口返回后覆盖 Banner 配置
					if (configRes.retCode === 1 && configRes.result) {
						const data = configRes.result
						this.bannerConfig = {
							DisplayMode: data.DisplayMode || 2,
							ImageUrl: data.ImageUrl || this.defaultImage,
							LeftImageUrl: data.LeftImageUrl || this.defaultLeftImage,
							RightImageUrl: data.RightImageUrl || this.defaultRightImage
						}
					}

					// 接口返回后覆盖分类数据（若接口有数据）
					if (categoriesRes.retCode === 1 && categoriesRes.result && categoriesRes.result.length > 0) {
						this.homepageCategories = categoriesRes.result
					}
					// 若接口无数据，保留默认的 DEFAULT_CATEGORIES
				} catch (error) {
					console.error('Failed to fetch homepage data:', error)
				} finally {
					this.loading = false
				}
			},

			// ============ 页面跳转 ============
			goToProjects() {
				this.$router.push({ name: 'Projects' })
			},
			goToMaterials() {
				this.$router.push({ name: 'Materials' })
			},
			goToStories() {
				this.$router.push({ name: 'Stories' })
			},
			goToCategory(categoryId, categoryName) {
				if (categoryName === 'denHolm United') {
					window.open('https://www.denholm-united.com', '_blank')
					return
				}
				this.$router.push({
					path: '/product/index',
					query: { CategoryID: categoryId }
				})
			},
			onViewAllClick() {
				 this.$router.push({ 
				    name: 'ProductIndex', 
				    query: { CategoryID: 0 } 
				  })
			},

			// ============ 滚动监听 ============
			handleScroll(e) {
				let scrollTop = 0
				if (e && e.target && typeof e.target.scrollTop === 'number') {
					scrollTop = e.target.scrollTop
				} else {
					scrollTop = window.scrollY || document.documentElement.scrollTop || 0
				}
				this.isScrolled = scrollTop > 50
			}
		},
		mounted() {
			this.fetchHomepageData()
			const homePage = this.$el
			if (homePage) {
				homePage.addEventListener('scroll', this.handleScroll)
			}
		},
		beforeDestroy() {
			const homePage = this.$el
			if (homePage) {
				homePage.removeEventListener('scroll', this.handleScroll)
			}
		}
	}
</script>

<style scoped>
	.home-page {
		width: 100%;
		height: 100vh;
		overflow-y: auto;
		position: relative;
		background: #fff;
		scrollbar-width: none;
		-ms-overflow-style: none;
	}
	.home-page::-webkit-scrollbar {
		display: none;
	}

	/* ============ Hero Section ============ */
	.hero-section {
		width: 100%;
		height: 816rem;
		margin-top: -123rem;
		position: relative;
		display: flex;
		flex-direction: row;
		overflow: hidden;
	}

	.hero-image-left,
	.hero-image-right {
		flex: 1;
		height: 100%;
		overflow: hidden;
	}
	.hero-image-left img,
	.hero-image-right img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.hero-image-single {
		width: 100%;
		height: 100%;
		overflow: hidden;
	}
	.hero-image-single img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	/* ===== Hero 加载遮罩 ===== */
	.hero-loading-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(255, 255, 255, 0.6);
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		z-index: 3;
		backdrop-filter: blur(2px);
		transition: opacity 0.3s ease;
	}

	.hero-spinner {
		width: 50rem;
		height: 50rem;
		border: 4rem solid rgba(0, 0, 0, 0.1);
		border-top: 4rem solid #000;
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
	}

	.hero-loading-text {
		margin-top: 16rem;
		font-size: 14rem;
		color: #333;
		font-family: 'Apercu', sans-serif;
		letter-spacing: 0.5rem;
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	.hero-content {
		position: absolute;
		bottom: 61rem;
		left: 50%;
		transform: translateX(-50%);
		text-align: center;
		color: #fff;
		z-index: 2;
		pointer-events: none;
	}
	.hero-content * {
		pointer-events: auto;
	}
	.hero-subtitle {
		font-size: 14rem;
		margin-bottom: 14rem;
		font-weight: 500;
	}
	.hero-title {
		font-size: 28rem;
		font-weight: 400;
	}

	/* ============ Collection Grid ============ */
	.collection-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 16rem;
		padding: 37rem 31rem 56rem;
	}
	.grid-item {
		text-align: center;
	}
	.image-wrapper {
		width: 100%;
		aspect-ratio: 3 / 4;
		background-color: #f5f5f5;
		margin-bottom: 20rem;
		overflow: hidden;
		cursor: pointer;
	}
	.image-wrapper img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
	.image-wrapper:hover img {
		border: 1rem solid #000;
	}
	.item-name {
		font-size: 14rem;
		color: #000;
		font-weight: 500;
		cursor: pointer;
	}

	/* Skeleton 加载占位 */
	.skeleton {
		background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
		background-size: 200% 100%;
		animation: shimmer 1.5s infinite;
	}
	.skeleton-text {
		height: 20rem;
		background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
		background-size: 200% 100%;
		animation: shimmer 1.5s infinite;
		border-radius: 4rem;
	}
	@keyframes shimmer {
		0% { background-position: -200% 0; }
		100% { background-position: 200% 0; }
	}

	/* ============ Project Banner ============ */
	.project-banner {
		width: 100%;
		height: 1008rem;
		background-size: cover;
		background-position: center;
		position: relative;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: center;
		padding-bottom: 69rem;
	}
	.banner-content {
		text-align: center;
		color: #fff;
	}
	.banner-subtitle {
		font-size: 16rem;
		margin-bottom: 14rem;
		font-weight: 500;
	}
	.banner-title {
		font-size: 30rem;
		font-weight: 400;
	}

	/* ============ Two Column Section ============ */
	.two-column-section {
		display: flex;
		padding: 47rem 39rem;
		gap: 20rem;
	}
	.column-item {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}
	.column-image-wrapper {
		width: 100%;
		aspect-ratio: 4/3;
		background-color: #f5f5f5;
		overflow: hidden;
	}
	.column-image-wrapper img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
	.column-title {
		font-size: 16rem;
		margin-bottom: 3rem;
	}
	.column-subtitle {
		font-size: 12rem;
		color: #000;
	}

	/* ============ Responsive ============ */
	@media (max-width: 768px) {
		.hero-section {
			flex-direction: column;
			height: auto;
		}
		.hero-image-left,
		.hero-image-right,
		.hero-image-single {
			height: 400rem;
			flex: none;
			width: 100%;
		}
		.hero-content {
			position: relative;
			bottom: auto;
			left: auto;
			transform: none;
			padding: 40rem 20rem;
			background: rgba(0,0,0,0.5);
			width: 100%;
		}
		.hero-title,
		.banner-title {
			font-size: 30rem;
		}
		.collection-grid {
			grid-template-columns: repeat(2, 1fr);
		}
		.two-column-section {
			flex-direction: column;
		}
	}
	@media (max-width: 480px) {
		.hero-image-left,
		.hero-image-right,
		.hero-image-single {
			height: 300rem;
		}
		.collection-grid {
			grid-template-columns: 1fr;
		}
	}
</style>